import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, Mail, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { inputCls, btn, btnLight, Card, H, errMsg } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ah = () => ({ withCredentials: true });
const fmt = (iso) => new Date(iso).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" });

export const Preferences = ({ l, lang }) => {
  const [p, setP] = useState(null);
  useEffect(() => { axios.get(`${API}/account/preferences`, ah()).then((r) => setP(r.data)).catch(() => setP({ newsletter: false, lang })); }, [lang]);
  const save = async (patch) => {
    try { const r = await axios.put(`${API}/account/preferences`, patch, ah()); setP(r.data); toast.success(l.saved); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!p) return null;
  return (
    <Card data-testid="account-preferences" className="max-w-xl">
      <H>{l.title}</H>
      <label className="flex items-start gap-3 text-sm text-stone-700"><input data-testid="pref-newsletter" type="checkbox" checked={p.newsletter} onChange={(e) => save({ newsletter: e.target.checked })} className="accent-[#C85A32] w-4 h-4 mt-0.5" /> {l.newsletter}</label>
      <label className="mt-5 block text-sm text-stone-700">{l.lang}
        <select data-testid="pref-lang" className={`${inputCls} mt-2 !w-40`} value={p.lang} onChange={(e) => save({ lang: e.target.value })}><option value="fr">Français</option><option value="en">English</option></select>
      </label>
    </Card>
  );
};

export const Support = ({ l, orders, config }) => {
  const [f, setF] = useState({ subject: "", message: "", order_number: "" });
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => axios.get(`${API}/account/messages`, ah()).then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API}/account/messages`, { ...f, order_number: f.order_number || null }, ah()); toast.success(l.sent); setF({ subject: "", message: "", order_number: "" }); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card data-testid="account-support">
        <H>{l.title}</H>
        <form onSubmit={send} className="space-y-3">
          <input data-testid="support-subject" required minLength={2} className={inputCls} placeholder={l.subject} value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} />
          <select data-testid="support-order" className={inputCls} value={f.order_number} onChange={(e) => setF({ ...f, order_number: e.target.value })}><option value="">{l.order}</option>{(orders || []).map((o) => <option key={o.id} value={o.order_number}>{o.order_number}</option>)}</select>
          <textarea data-testid="support-message" required minLength={5} rows={5} className={inputCls} placeholder={l.message} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
          <button data-testid="support-send" disabled={busy} className={btn}>{l.send}</button>
        </form>
        <div className="mt-6 pt-5 border-t border-stone-100 text-sm text-stone-600 space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-400">{l.other}</p>
          {config.company?.email && <p className="flex items-center gap-2"><Mail size={13} /> <a href={`mailto:${config.company.email}`} className="underline">{config.company.email}</a></p>}
          <p className="flex items-center gap-2"><MessageCircle size={13} /> <Link to="/#contact" className="underline">Formulaire de contact</Link></p>
        </div>
      </Card>
      <Card data-testid="account-support-history">
        <H>{l.history}</H>
        {rows.length === 0 ? <p className="text-sm text-stone-500">—</p> : (
          <ul className="divide-y divide-stone-100 text-sm">
            {rows.map((m) => (
              <li key={m.id} className="py-3" data-testid={`support-msg-${m.id}`}>
                <div className="flex items-center justify-between gap-3"><p className="text-stone-900">{m.subject}{m.order_number ? <span className="ml-2 font-mono text-[10px] text-[#C85A32]">{m.order_number}</span> : null}</p><span className="text-[10px] font-mono text-stone-400">{fmt(m.created_at)}</span></div>
                <p className="mt-1 text-stone-600 whitespace-pre-line">{m.message}</p>
                {m.reply ? <div className="mt-2 rounded-xl bg-[#FAF7F2] border border-stone-200 p-3"><p className="text-[10px] font-mono uppercase tracking-wide text-[#4A624E]">{l.reply}</p><p className="mt-1 text-stone-700 whitespace-pre-line">{m.reply}</p></div> : <p className="mt-1 text-[11px] text-stone-400">{l.pending}</p>}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
};

export const DataPrivacy = ({ l, logout }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const exportData = async () => {
    const r = await axios.get(`${API}/account/export`, ah());
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "nura-mes-donnees.json" }); a.click();
  };
  const del = async (e) => {
    e.preventDefault();
    if (!window.confirm(l.deleteBody)) return;
    setBusy(true);
    try { await axios.post(`${API}/account/delete`, { password: pw }, ah()); toast.success(l.deleted); await logout(); }
    catch (err) { toast.error(errMsg(err)); setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card data-testid="account-data">
        <H>{l.title}</H>
        <p className="text-sm text-stone-600 leading-relaxed">{l.body}</p>
        <button data-testid="data-export" onClick={exportData} className={`${btn} mt-5`}><Download size={13} /> {l.export}</button>
        <p className="mt-4 text-[11px] text-stone-400"><Link to="/p/confidentialite" className="underline">Politique de confidentialité</Link> · <Link to="/p/cookies" className="underline">Cookies</Link></p>
      </Card>
      <Card data-testid="account-delete" className="border-rose-100">
        <H>{l.deleteTitle}</H>
        <p className="text-sm text-stone-600 leading-relaxed">{l.deleteBody}</p>
        <form onSubmit={del} className="mt-4 flex flex-wrap gap-2">
          <input data-testid="delete-password" required type="password" className={`${inputCls} !w-56`} placeholder={l.pw} value={pw} onChange={(e) => setPw(e.target.value)} />
          <button data-testid="delete-submit" disabled={busy} className={`${btnLight} hover:!border-rose-600 hover:!text-rose-700`}><Trash2 size={13} /> {l.delete}</button>
        </form>
      </Card>
    </div>
  );
};
